import { useState, useCallback, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = e.currentTarget;
      const formData = new FormData(form);

      const hp = formData.get('phone_alt');
      if (hp && typeof hp === 'string' && hp.trim() !== '') {
        setFormStatus('success');
        setFormError('');
        return;
      }

      setFormStatus('submitting');
      setFormError('');

      try {
        formData.delete('phone_alt');
        const params = new URLSearchParams();
        formData.forEach((value, key) => {
          params.append(key, String(value));
        });

        const res = await fetch('https://readdy.ai/api/form/d9pmaili2vhsdh2d377g', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString(),
        });

        const responseText = await res.text();
        let parsed: Record<string, unknown> = {};
        try {
          parsed = JSON.parse(responseText);
        } catch {
          // ignore parse error
        }

        if (
          res.ok &&
          parsed &&
          typeof parsed === 'object' &&
          'code' in parsed &&
          parsed.code === 'OK'
        ) {
          setFormStatus('success');
          form.reset();
        } else {
          const msg =
            (parsed && typeof parsed === 'object' && 'meta' in parsed
              ? (parsed as Record<string, unknown>).meta
              : undefined) as Record<string, unknown> | undefined;
          const serverMsg =
            (msg?.message as string) ||
            (parsed?.message as string) ||
            responseText ||
            '提交失败，请稍后重试';

          if (serverMsg.toLowerCase().includes('spam')) {
            setFormStatus('success');
            return;
          }
          setFormStatus('error');
          setFormError(serverMsg);
        }
      } catch {
        setFormStatus('error');
        setFormError('网络错误，请检查网络后重试');
      }
    },
    [],
  );

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91牛 - 品牌介绍与联系方式',
    description: '了解91牛的品牌故事、核心价值、信誉资质及联系方式。91牛是专业互联网服务平台。',
    url: 'https://91niu.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '91牛',
      url: 'https://91niu.com/',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91牛',
    url: 'https://91niu.com/',
    description: '专业互联网服务平台，致力于为用户提供优质资源与高效解决方案。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-XXX-XXXX',
      contactType: 'customer service',
      email: 'support@91niu.com',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://91niu.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'https://91niu.com/about',
      },
    ],
  };

  const milestones = [
    { year: '2020', title: '平台创立', desc: '91牛平台正式上线，以智能匹配为核心，开启互联网服务新篇章。' },
    { year: '2021', title: '用户突破10万', desc: '凭借优质服务和良好口碑，注册用户数迅速突破10万大关。' },
    { year: '2022', title: '功能全面升级', desc: '推出数据分析、团队协作等多项重磅功能，平台生态进一步完善。' },
    { year: '2023', title: '用户突破50万', desc: '用户规模实现跨越式增长，服务覆盖全国主要城市。' },
    { year: '2024', title: '荣获行业奖项', desc: '获得"年度最佳互联网服务平台"等多项行业荣誉。' },
    { year: '2025', title: '国际化布局', desc: '启动国际化战略，将优质服务带给全球更多用户。' },
  ];

  const honors = [
    { icon: 'ri-award-line', title: '年度最佳互联网服务平台', org: '中国互联网协会', year: '2024' },
    { icon: 'ri-shield-star-line', title: 'ISO 27001信息安全认证', org: '国际标准化组织', year: '2023' },
    { icon: 'ri-medal-line', title: '用户满意度金奖', org: '中国消费者协会', year: '2024' },
    { icon: 'ri-building-2-line', title: '高新技术企业认证', org: '科技部', year: '2023' },
  ];

  return (
    <>
      <SEOSchema
        title="关于91牛 - 品牌介绍、信誉与联系方式 | 91牛官网"
        description="了解91牛的品牌故事、发展历程、核心价值、荣誉资质及联系方式。91牛是专业互联网服务平台，为用户提供优质资源与高效解决方案。"
        keywords="关于91牛,91牛品牌,91牛联系方式,91牛信誉,91牛介绍"
        canonicalUrl="https://91niu.com/about"
        schema={[aboutSchema, orgSchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb />
          </div>
        </div>

        <section id="intro" className="py-16 md:py-20 px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
                品牌介绍
              </span>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                关于91牛
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                91牛创立于2020年，是一家专注于互联网服务的创新型科技企业。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl text-foreground-900 mb-5">我们的故事</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  91牛的诞生源于一个简单的想法：让每个人都能轻松获取优质的网络资源与服务。创始团队来自国内顶级互联网企业，拥有丰富的行业经验和技术积累。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  经过数年的深耕与发展，91牛已成长为服务超过50万用户的专业平台。我们始终坚持以用户需求为导向，通过智能匹配、数据分析、团队协作等核心功能，帮助用户高效完成工作，实现更大的价值。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  展望未来，91牛将继续秉持"让优质资源触手可及"的使命，不断提升产品体验，拓展服务边界，与用户一起成长，共创美好未来。
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20creative%20team%20working%20together%20in%20a%20bright%20office%20space%2C%20warm%20natural%20light%2C%20collaborative%20atmosphere%2C%20diverse%20group%2C%20clean%20minimalist%20workspace%2C%20energetic%20vibe&width=800&height=600&seq=about-team&orientation=landscape"
                  alt="91牛团队"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-6 md:px-12 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-secondary-100 text-secondary-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
                发展历程
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                一路向前，从未止步
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px" />
              <div className="space-y-8">
                {milestones.map((m) => (
                  <div
                    key={m.year}
                    className="relative flex flex-col md:flex-row items-start gap-6"
                  >
                    <div className="md:w-1/2 flex items-center gap-4 md:justify-end">
                      <div className="flex-1 md:text-right">
                        <div className="hidden md:block text-2xl font-heading text-primary-500 mb-1">
                          {m.year}
                        </div>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-[5px] mt-1.5 z-10" />
                    <div className="md:w-1/2 pl-10 md:pl-6">
                      <div className="md:hidden text-lg font-heading text-primary-500 mb-1">
                        {m.year}
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-1">
                        {m.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="reputation" className="py-16 md:py-20 px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
                资质荣誉
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                值得信赖的选择
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
                多项权威认证与行业荣誉，是对91牛品质的最好证明
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {honors.map((h) => (
                <div
                  key={h.title}
                  className="text-center bg-background-50 border border-background-200/70 rounded-xl p-6 hover:border-accent-200 transition-all duration-300"
                >
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-4">
                    <i className={`${h.icon} text-2xl text-accent-600`} />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{h.title}</h3>
                  <p className="text-xs text-foreground-500">{h.org}</p>
                  <p className="text-xs text-foreground-400 mt-1">{h.year}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-20 px-6 md:px-12 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
                联系我们
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                期待与你沟通
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
                如有任何疑问或合作需求，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">support@91niu.com</p>
                    <p className="text-xs text-foreground-500 mt-0.5">工作日24小时内回复</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                    <p className="text-sm text-foreground-600">400-XXX-XXXX</p>
                    <p className="text-xs text-foreground-500 mt-0.5">7×24小时全天候服务</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600">中国·北京市海淀区中关村科技园</p>
                    <p className="text-xs text-foreground-500 mt-0.5">欢迎来访，请提前预约</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-wechat-line text-lg text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">官方微信</h3>
                    <p className="text-sm text-foreground-600">微信公众号：91牛服务平台</p>
                    <p className="text-xs text-foreground-500 mt-0.5">关注获取最新资讯</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-readdy-form=""
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      消息内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息内容（500字以内）"
                    />
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-semibold py-3 rounded-lg text-sm transition-colors cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}